import type { PersistedDefaultTabProps, TabProps } from '@talos/kyoko';
import type { GroupviewPanelState } from 'dockview';
import { Navigate, useParams } from 'react-router-dom';
import { SubTabBar } from '../../../../components/Mobile/TabBar/SubTabBar/SubTabBar';
import type { NavigationItem } from '../../../../tokens/navigationPaths';
import type { LayoutSpec, PanelConfig } from '../../../../types/LayoutConfig';
import { TradingPlaceholder } from '../TradingPlaceholder';
import { getBlotterTabRoute } from './navRoutes';
import { usePanelTabNavItems } from './usePanelTabNavItems';

export interface MobileBlotterPanelProps<T extends TabProps> {
  layout: LayoutSpec;
  panel: GroupviewPanelState;
  panelConfig: PanelConfig | undefined;
  defaultTabs: PersistedDefaultTabProps<T>[];
}

/**
 * Shared mobile blotter shell: resolves persisted-or-default tabs, redirects to the default tab when the
 * current URL targets the panel root (no `:tabId` segment), and renders the SubTabBar for the resolved tabs.
 * Body content is intentionally a temporary placeholder until dedicated mobile blotter views are built.
 */
export function MobileBlotterPanel<T extends TabProps>({
  layout,
  panel,
  panelConfig,
  defaultTabs,
}: MobileBlotterPanelProps<T>) {
  const { tabId } = useParams<{ tabId?: string }>();
  const { items, selectedTabId } = usePanelTabNavItems({
    layout,
    panelId: panel.id,
    panelConfig,
    defaultTabs,
  });

  if (tabId == null && selectedTabId != null) {
    return <Navigate to={getBlotterTabRoute(layout, panel.id, selectedTabId)} replace />;
  }

  const parent: NavigationItem = {
    id: panel.id,
    label: panel.title,
    children: items,
  };

  return (
    <>
      <TradingPlaceholder label={panel.title ?? ''} />
      {items.length > 0 && <SubTabBar parent={parent} />}
    </>
  );
}
